import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from '../../../hooks/useTranslation';

const WeekThree = ({ onBack, onNext, onProgressUpdate, onReflectionUpdate, initialCompletedActivities = [], initialReflectionAnswers = {}, friend }) => {
  const [completedActivities, setCompletedActivities] = useState(initialCompletedActivities);
  const [currentActivityIndex, setCurrentActivityIndex] = useState(0);
  const [reflectionAnswers, setReflectionAnswers] = useState({});
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const isInitialMount = useRef(true);
  const prevInitialActivities = useRef(initialCompletedActivities);

  const activities = [
    {
      id: 'go-deeper',
      type: 'exercise',
      title: 'Step 1: Go One Level Deeper',
      description: 'Ask permission first, then choose ONE question',
      content: `**STEP 1: GO ONE LEVEL DEEPER**

Ask permission first:

"Can I ask you something a bit more personal?"

Choose ONE question:

- "What's been weighing on you lately?"

- "What's something you're proud of right now?"

- "What's a change you've been navigating recently?"

Rules:

- One question

- Follow-ups matter more than new questions

- Silence is allowed`,
      estimatedTime: '15 minutes'
    },
    {
      id: 'share-truth',
      type: 'exercise',
      title: 'Step 2: Share a Small, Real Truth',
      description: 'Choose a Level 1 or Level 2 share',
      content: `**STEP 2: SHARE A SMALL, REAL TRUTH**

Choose a Level 1 or Level 2 share:

- "I've felt a bit lonely at times lately."

- "I'm working on being more consistent with people."

- "Something's been stressing me more than I expected."

Formula:

Experience → Feeling → Meaning

No fixing. No dumping.`,
      estimatedTime: '10 minutes'
    },
    {
      id: 'deep-listening',
      type: 'exercise',
      title: 'Step 3: Deep Listening',
      description: 'During the conversation',
      content: `**STEP 3: DEEP LISTENING**

During the conversation:

- Reflect meaning

- Validate emotion

- Avoid advice unless asked

Success signal:

They talk more than usual and seem relaxed afterward.`,
      estimatedTime: '20 minutes'
    },
    {
      id: 'reflection',
      type: 'exercise',
      title: 'Week 3 Reflection',
      description: 'Answer briefly',
      content: `**WEEK 3 REFLECTION**

- Did depth feel mutual?

- Did I stay grounded?

- What strengthened the connection most?`,
      estimatedTime: '10 minutes'
    }
  ];

  // Load completed activities from Firebase or localStorage fallback
  useEffect(() => {
    // Only sync from initialCompletedActivities on initial mount
    // This prevents resetting local state when user is actively checking/unchecking
    if (isInitialMount.current) {
      if (currentUser) {
        setCompletedActivities(initialCompletedActivities || []);
        prevInitialActivities.current = initialCompletedActivities;
      } else {
        const saved = localStorage.getItem('week3CompletedActivities');
        if (saved) {
          setCompletedActivities(JSON.parse(saved));
        }
      }
      isInitialMount.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Set current activity to first incomplete activity on mount
  useEffect(() => {
    const firstIncompleteIndex = activities.findIndex(
      activity => !completedActivities.includes(activity.id)
    );
    if (firstIncompleteIndex !== -1) {
      setCurrentActivityIndex(firstIncompleteIndex);
    } else {
      // All activities completed, show the last one
      setCurrentActivityIndex(activities.length - 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load reflection answers when activity changes
  useEffect(() => {
    if (currentActivityIndex >= 0 && activities[currentActivityIndex]) {
      const activity = activities[currentActivityIndex];
      
      // Use initialReflectionAnswers from props (Firebase) if available, otherwise fallback to localStorage
      if (Object.keys(initialReflectionAnswers).length > 0) {
        setReflectionAnswers(initialReflectionAnswers);
      } else {
        const savedAnswers = localStorage.getItem(`week3Reflections_${activity.id}`);
        if (savedAnswers) {
          setReflectionAnswers(JSON.parse(savedAnswers));
        } else {
          setReflectionAnswers({});
        }
      }
    }
  }, [currentActivityIndex, initialReflectionAnswers]);

  const handleReflectionChange = (questionKey, value) => {
    const newAnswers = {
      ...reflectionAnswers,
      [questionKey]: value
    };
    setReflectionAnswers(newAnswers);
    
    // Save to Firebase if callback provided, otherwise save to localStorage
    if (onReflectionUpdate) {
      onReflectionUpdate(newAnswers);
    } else {
      // Fallback to localStorage for non-authenticated users
      if (currentActivityIndex >= 0 && activities[currentActivityIndex]) {
        const activity = activities[currentActivityIndex];
        localStorage.setItem(`week3Reflections_${activity.id}`, JSON.stringify(newAnswers));
      }
    }
  };

  // Check if a line is a question/reflection prompt
  const isQuestion = (line) => {
    const trimmed = line.trim();
    return (
      trimmed.includes('?') ||
      trimmed.toLowerCase().startsWith('answer') ||
      trimmed.toLowerCase().startsWith('reflect') ||
      (trimmed.startsWith('-') && (trimmed.includes('?') || trimmed.toLowerCase().includes('what') || trimmed.toLowerCase().includes('how')))
    );
  };

  const toggleActivity = (activityId) => {
    setCompletedActivities(prevCompleted => {
      const newCompleted = prevCompleted.includes(activityId)
        ? prevCompleted.filter(id => id !== activityId)
        : [...prevCompleted, activityId];
      
      // Always pass the array to onProgressUpdate, which will calculate the length
      if (!currentUser) {
        localStorage.setItem('week3CompletedActivities', JSON.stringify(newCompleted));
      }
      onProgressUpdate(newCompleted);
      
      return newCompleted;
    });
  };

  const handleNextActivity = () => {
    if (currentActivityIndex < activities.length - 1) {
      setCurrentActivityIndex(currentActivityIndex + 1);
    }
  };

  const handlePreviousActivity = () => {
    if (currentActivityIndex > 0) {
      setCurrentActivityIndex(currentActivityIndex - 1);
    }
  };

  const currentActivity = activities[currentActivityIndex];
  const isCurrentActivityCompleted = completedActivities.includes(currentActivity?.id);
  const canGoNext = currentActivityIndex < activities.length - 1;
  const canGoPrevious = currentActivityIndex > 0;
  const allActivitiesCompleted = completedActivities.length === activities.length;

  const getActivityIcon = (type) => {
    switch (type) {
      case 'video': return '🎥';
      case 'article': return '📖';
      case 'exercise': return '✏️';
      case 'challenge': return '🎯';
      default: return '📝';
    }
  };

  return (
    <div className="week-container">
      <div className="week-header">
        <button className="back-btn" onClick={onBack}>
          ← Back to Overview
        </button>
        <div className="week-title">
          <h1>Week 3: Deepen with Consent</h1>
          <p><strong>Goal:</strong> Increase emotional depth only where safety and reciprocity exist.</p>
        </div>
        <div className="week-progress">
          <span className="progress-text">
            {completedActivities.length}/{activities.length} {t('activitiesCompleted')}
          </span>
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${(completedActivities.length / activities.length) * 100}%` }}
            ></div>
          </div>
        </div>
      </div>

      <div className="activity-progress-indicator">
        <span className="activity-counter">
          Activity {currentActivityIndex + 1} of {activities.length}
        </span>
      </div>

      <div className="activities-list">
        {currentActivity && (
          <div 
            className={`activity-card ${isCurrentActivityCompleted ? 'completed' : ''}`}
          >
            <div className="activity-header">
              <div className="activity-icon">
                {getActivityIcon(currentActivity.type)}
              </div>
              <div className="activity-info">
                <h3>{currentActivity.title}</h3>
                <p className="activity-description">{currentActivity.description}</p>
                <span className="activity-time">{currentActivity.estimatedTime}</span>
              </div>
              {isCurrentActivityCompleted && (
                <div className="activity-completed-badge">
                  <span>✓ Completed</span>
                </div>
              )}
            </div>
            
            <div className="activity-content">
              <div className="content-text">
                {currentActivity.content.split('\n').map((line, lineIndex) => {
                  const trimmedLine = line.trim();
                  
                  // Skip empty lines
                  if (!trimmedLine) {
                    return <br key={lineIndex} />;
                  }
                  
                  // Bold headers (lines starting with **)
                  if (trimmedLine.startsWith('**') && trimmedLine.endsWith('**')) {
                    return (
                      <p key={lineIndex} className="bold-text">
                        {trimmedLine.replace(/\*\*/g, '')}
                      </p>
                    );
                  }
                  
                  // Questions/Reflections - add text input
                  if (isQuestion(trimmedLine)) {
                    const questionKey = `${currentActivity.id}_q${lineIndex}`;
                    const questionText = trimmedLine.startsWith('-') 
                      ? trimmedLine.substring(1).trim() 
                      : trimmedLine;
                    
                    return (
                      <div key={lineIndex} className="reflection-question">
                        <label className="reflection-question-label">{questionText}</label>
                        <textarea
                          className="reflection-textarea"
                          placeholder="Write your thoughts here..."
                          value={reflectionAnswers[questionKey] || ''}
                          onChange={(e) => handleReflectionChange(questionKey, e.target.value)}
                        />
                      </div>
                    );
                  }
                  
                  // Regular paragraphs
                  return (
                    <p key={lineIndex}>
                      {trimmedLine}
                    </p>
                  );
                })}
              </div>
              
              <div className="activity-completion-section">
                {!isCurrentActivityCompleted ? (
                  <button 
                    className="btn btn-complete-activity"
                    onClick={() => toggleActivity(currentActivity.id)}
                  >
                    ✓ Mark as Complete
                  </button>
                ) : (
                  <div className="activity-completed-actions">
                    <div className="completion-message">
                      <span className="completion-icon">✓</span>
                      <span>Activity completed!</span>
                    </div>
                    {canGoNext && (
                      <button 
                        className="btn btn-primary btn-continue"
                        onClick={handleNextActivity}
                      >
                        Continue to Next Activity →
                      </button>
                    )}
                    {!canGoNext && allActivitiesCompleted && (
                      <button className="btn btn-primary btn-continue" onClick={onNext}>
                        {t('completeWeek3Start4')}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="week-navigation">
        {canGoPrevious && (
          <button 
            className="btn btn-secondary btn-back-activity" 
            onClick={handlePreviousActivity}
          >
            ← Previous Activity
          </button>
        )}
      </div>
    </div>
  );
};

export default WeekThree;